import './Schedule.css';
import React, { useEffect, useState } from 'react';
import Papa from 'papaparse';
import { Link } from 'react-router-dom';
import {
  normalizeSpeakerName,
  resolveScheduleSpeaker,
  toSpeakerSlug,
} from '../../utils/speakerLinks';

const splitPeople = (value) =>
  value ? value.split(';').map((person) => person.trim()).filter(Boolean) : [];

const LinkedPerson = ({ entry, speakerLookup }) => {
  const commaIndex = entry.indexOf(',');
  const namesText = commaIndex === -1 ? entry : entry.slice(0, commaIndex);
  const suffix = commaIndex === -1 ? '' : entry.slice(commaIndex);
  const displayNames = namesText.split('/').map((name) => name.trim()).filter(Boolean);
  const resolvedNames = displayNames.map((name) => ({
    displayName: name,
    canonicalName: resolveScheduleSpeaker(name, speakerLookup),
  }));

  if (!resolvedNames.some(({ canonicalName }) => canonicalName)) {
    return entry;
  }

  return (
    <>
      {resolvedNames.map(({ displayName, canonicalName }, index) => (
        <React.Fragment key={displayName}>
          {index > 0 ? ' / ' : ''}
          {canonicalName ? (
            <Link to={`/speakers?person=${toSpeakerSlug(canonicalName)}`}>
              {displayName}
            </Link>
          ) : displayName}
        </React.Fragment>
      ))}
      {suffix}
    </>
  );
};

const DetailGroup = ({ label, people, speakerLookup }) => {
  const entries = splitPeople(people);

  if (!entries.length) return null;

  return (
    <div className="schedule-detail-group">
      <h4>{label}</h4>
      <ul>
        {entries.map((person) => (
          <li key={person}>
            <LinkedPerson entry={person} speakerLookup={speakerLookup} />
          </li>
        ))}
      </ul>
    </div>
  );
};

const Accordion = ({ session, speakerLookup }) => {
  const [isOpen, setIsOpen] = useState(false);
  const isExpandable = Boolean(
    splitPeople(session.Speakers).length ||
    splitPeople(session.Moderators).length ||
    splitPeople(session.Participants).length
  );

  const headerContent = (
    <>
      <span className="session-info-left">
        <span className="session-name">{session.Session}</span>{' '}
        <span className="session-time">({session['Session Time']})</span>
      </span>
      <span className="session-info-right">{session['Session Info'] || ''}</span>
      {isExpandable ? (
        <span className="accordion-chevron" aria-hidden="true">
          {isOpen ? '−' : '+'}
        </span>
      ) : null}
    </>
  );

  return (
    <div className={`accordion ${isExpandable ? '' : 'non-clickable'}`}>
      {isExpandable ? (
        <button
          type="button"
          className="accordion-header"
          aria-expanded={isOpen}
          onClick={() => setIsOpen((open) => !open)}
        >
          {headerContent}
        </button>
      ) : (
        <div className="accordion-header">{headerContent}</div>
      )}
      {isOpen ? (
        <div
          className="accordion-content"
          data-testid={`schedule-details-${session.Session}`}
        >
          <DetailGroup label="Participants" people={session.Participants} speakerLookup={speakerLookup} />
          <DetailGroup label="Panelists" people={session.Speakers} speakerLookup={speakerLookup} />
          <DetailGroup label="Moderator" people={session.Moderators} speakerLookup={speakerLookup} />
        </div>
      ) : null}
    </div>
  );
};

const Schedule = () => {
  const [scheduleData, setScheduleData] = useState([]);
  const [speakerLookup, setSpeakerLookup] = useState({});
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    const handleLoadFailure = () => {
      setScheduleData([]);
      setLoadError(true);
    };

    Papa.parse(`${process.env.PUBLIC_URL}/schedule.csv`, {
      download: true,
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        if (!Array.isArray(result?.data)) {
          handleLoadFailure();
          return;
        }

        const usableRows = result.data.filter(
          (session) =>
            session &&
            typeof session.Session === 'string' &&
            session.Session.trim()
        );

        if (!usableRows.length) {
          handleLoadFailure();
          return;
        }

        setLoadError(false);
        setScheduleData(usableRows);
      },
      error: handleLoadFailure,
    });

    Papa.parse(`${process.env.PUBLIC_URL}/speakers.csv`, {
      download: true,
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        const lookup = {};
        if (Array.isArray(result?.data)) {
          result.data.forEach((speaker) => {
            const name = speaker?.['Speaker Name'];
            if (typeof name === 'string' && name.trim()) {
              lookup[normalizeSpeakerName(name)] = name.trim();
            }
          });
        }
        setSpeakerLookup(lookup);
      },
    });
  }, []);

  return (
    <section className="schedule-container" aria-labelledby="schedule-heading">
      <h1 id="schedule-heading">Tentative Schedule</h1>
      <p className="schedule-date">Friday, September 25, 2026</p>
      {loadError ? (
        <p role="alert" className="schedule-error">
          Schedule details are temporarily unavailable. Please check back soon.
        </p>
      ) : (
        <div className="schedule-list">
          {scheduleData.map((session) => (
            <Accordion
              key={`${session.Session}-${session['Session Time']}`}
              session={session}
              speakerLookup={speakerLookup}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default Schedule;
